import './styles.scss';

import { Button, Card, Form, ProgressBar } from 'react-bootstrap';
import { ReactNode, useState } from 'react';
import { Validation, bytesToSize, getClasses, uuid } from '@/utils';

import { FileUploader } from 'react-drag-drop-files';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import axios from 'axios';
import md5 from 'md5';
import useLocale from '@/hooks/useLocale';

export type GenericFileUploadOptions = {
  showFiles?: boolean;
  supportedExtensions?: string[];
  label?: string;
  multiple?: boolean;
  hideSuccessful?: boolean;
};

type GenericFileUploadProps = {
  endpoint: ((file) => Promise<string | string[]>) | string;
  options?: GenericFileUploadOptions;
  onSuccess?: () => Promise<any>;
  onError?: () => Promise<any>;
  onCancel?: () => Promise<any>;
  onContinue?: (files) => Promise<any>;
  locale?: Record<string, string>;
  condensed?: boolean;
  skipPreview?: boolean;
};

export enum FileUploadStage {
  'Processing' = 'PROCESSING',
  'Uploading' = 'UPLOADING',
  'Done' = 'DONE',
  'Error' = 'ERROR',
  'Uploaded' = 'UPLOADED',
  'Deleted' = 'DELETED',
}

export type GenericFileType = {
  file: File;
  md5: string;
  id: string;
  progress: number;
  stage: FileUploadStage;
  previewUrl?: string;
};

type GenericFileProps = {
  name: any;
  size?: any;
  progress?: number;
  error?: GenericErrorType;
  locale?: Record<string, string>;
  stage?: FileUploadStage;
  preview?: string;
  onDelete?: () => void;
  children?: ReactNode;
};

type GenericErrorType = {
  message?: string;
};

type GenericFileUploadState = {
  files: GenericFileType[];
  errors: Record<string, GenericErrorType>;
  loading: boolean;
};

// TODO: Change the way uploading works when given condensed={true}
const GenericFileUpload = ({
  endpoint,
  locale: customLocale = {},
  options = {},
  onError,
  onSuccess,
  onContinue,
  onCancel,
  condensed = false,
  skipPreview = false,
}: GenericFileUploadProps) => {
  const [state, setState] = useState<GenericFileUploadState>({
    files: [],
    errors: {},
    loading: false,
  });
  const { files, errors, loading } = state;
  const { showFiles = true, label = '', multiple = true, supportedExtensions = [], hideSuccessful = false } = options;
  const locale = useLocale(customLocale);

  const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const processFileForUpload = async (file: File): Promise<GenericFileType> => {
    try {
      await readFileAsArrayBuffer(file);
      const id = uuid();
      return {
        file,
        md5: md5(file.name, file.size, file.type),
        id,
        progress: 0,
        stage: FileUploadStage.Processing,
      };
    } catch (error) {
      if (onError) await onError();
      throw error;
    }
  };

  const isFileSupported = (file) => {
    const fileExtensionRegex = /[0-9a-z]+$/i;
    return options.supportedExtensions?.length > 0 ? supportedExtensions.includes(file.name.match(fileExtensionRegex)[0]) : true;
  };

  const handleCancel = async () => {
    onCancel?.();
    setState((current) => ({ ...current, files: [] }));
  };

  const handleContinue = async (files) => {
    onContinue?.(files);
    setState((current) => ({ ...current, files: [] }));
  };

  const uploadFile = async (processedFile: GenericFileType, url: string, contentType?: string) => {
    try {
      if (!processedFile) throw new Error('Nothing to upload.');
      if (url === null || url === undefined) throw new Error('No url provided.');
      if (!isFileSupported(processedFile.file)) throw new Error('File type not supported');
      if (Validation.isNil(processedFile.file.type)) {
        throw new Error('No contentType provided.');
      }
      const headers: any = {
        'Content-Type': contentType !== undefined ? contentType : processedFile.file.type,
      };
      // If the url is an empty string, it's because we are restoring a deleted file, no need to upload it again, just mark it as done
      if (url !== '') {
        await axios.put(url, processedFile.file, {
          headers,
        });
      }
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.id === processedFile.id ? { ...f, progress: 100, stage: FileUploadStage.Done } : f)),
      }));
    } catch (err) {
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.id === processedFile.id ? { ...f, progress: 100, stage: FileUploadStage.Error } : f)),
        errors: { ...current.errors, [processedFile.id]: { message: err.message } },
      }));
    }
  };

  const getEndpointUrl = async (file: GenericFileType) => {
    try {
      const response = typeof endpoint === 'string' ? endpoint : await endpoint(file);
      let url = response;
      let contentType = file.file.type;
      if (Array.isArray(response)) {
        url = response[0];
        contentType = response[1] !== undefined ? response[1] : contentType;
      }
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.id === file.id ? { ...f, stage: FileUploadStage.Uploading } : f)),
      }));
      return response;
    } catch (err) {
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.id === file.id ? { ...f, progress: 100, stage: FileUploadStage.Error } : f)),
        errors: { ...current.errors, [file.id]: { message: err.message } },
      }));
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const processedFile = await processFileForUpload(file);
      setState((current) => ({
        ...current,
        files: [...current.files, ...[processedFile]],
        errors: { ...current.errors, [processedFile.id]: undefined },
      }));
      const response = await getEndpointUrl(processedFile);
      const url = Array.isArray(response) ? response[0] : response;
      const contentType = Array.isArray(response) ? response[1] : file.type;
      await uploadFile(processedFile, url, contentType);
    } catch (err) {
      if (onError) await onError();
    }
  };

  const handleChange = async (files: File[] | File) => {
    setState((current) => ({ ...current, loading: true }));
    files = multiple ? Object.values(files) : [files];
    try {
      const promises = files.map(async (file) => handleUpload(file));
      await Promise.allSettled(promises);
      if (onSuccess) {
        const res = await onSuccess();
        if (res) {
          setState((current) => ({
            ...current,
            files: current.files.map((f) => {
              const match = Array.isArray(res) ? res.find((r) => r.md5 === f.md5) : undefined;
              return { ...f, previewUrl: match ? match.url : undefined };
            }),
          }));
        }
      }
    } catch (e) {
      if (onError) await onError();
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  return (
    <div className={getClasses('FileUpload', 'flex flex-column', condensed ? 'condensed' : undefined)}>
      <Form.Group className="mb-3">
        <Form.Label id="file-upload-label">{!(files || []).length && <div>{label}</div>}</Form.Label>
        <FileUploader classes="drag-and-drop border border-primary rounded text-center" handleChange={handleChange} multiple={multiple}>
          <>
            <div className="drag-area d-flex flex-column mb-1">
              <i className="upload-icon fa fa-2x fa-file-lines text-secondary my-2" />
              <div className="m-0 d-flex gap-1 justify-content-center">
                <span>{locale('Drag and Drop or')}</span>
                <strong>{locale('Browse')}</strong>
              </div>
            </div>
            {!condensed && (
              <Button className="mt-2 px-5" name="SUBMIT_UPLOAD">
                {locale('Upload')}
              </Button>
            )}
          </>
        </FileUploader>
      </Form.Group>
      {!condensed && (
        <div className="d-flex flex-column gap-3 width-100">
          {showFiles &&
            (files || [])
              .filter(({ stage }) => !hideSuccessful || stage !== FileUploadStage.Done)
              .map(({ file: { name, size }, id, progress, stage, previewUrl }, index) => (
                <GenericFile
                  key={index}
                  name={name}
                  size={size}
                  progress={progress}
                  error={errors[id]}
                  stage={stage || FileUploadStage.Processing}
                  locale={customLocale}
                  preview={skipPreview ? undefined : previewUrl}
                />
              ))}
        </div>
      )}
      <div className="justify-content-end flex gap-2">
        {onCancel && (
          <Button className="mt-2 px-5" name="CANCEL" onClick={handleCancel} variant="secondary">
            {locale('Cancel')}
          </Button>
        )}
        {onContinue && (
          <Button className="mt-2 px-5" name="CONTINUE" disabled={files?.length === 0 || loading} onClick={() => handleContinue(files)}>
            {locale('Continue')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const GenericFile = ({
  name,
  size,
  progress,
  error,
  stage,
  locale: customLocale,
  preview,
  onDelete,
  children,
}: GenericFileProps) => {
  const locale = useLocale(customLocale);
  const messageMap = {
    PROCESSING: <span className="GenericFile-Stage text-primary">{locale('Processing')}</span>,
    UPLOADING: <span className="GenericFile-Stage text-primary">{locale('Uploading')}</span>,
    DONE: <span className="GenericFile-Stage text-success">{locale('Complete')}</span>,
    ERROR: <span className="GenericFile-Stage text-danger">{locale('Upload Failed')}</span>,
    DELETED: <span className="GenericFile-Stage">{locale('Deleted')}</span>,
  };
  const borderColorMap: Record<string, string> = {
    PROCESSING: 'primary',
    UPLOADED: 'primary',
    DONE: 'success',
    ERROR: 'danger',
    UPLOADING: 'primary',
  };
  const canPreview = !!preview && !error;

  return (
    <Card className="GenericFile d-flex flex-row position-relative px-0" border={borderColorMap[stage]}>
      <Card.Body className="GenericFile-Icon d-flex flex-grow-0 justify-content-center align-items-center">
        <i className="sv sv-file-image fs-1" />
      </Card.Body>
      <Card.Body className="GenericFile-Details position-relative d-flex justify-content-center flex-column flex-grow-1 ps-0">
        <div className="d-flex w-100 gap-2">
          <Tippy content={name || 'Untitled'} placement="top">
            <span className="GenericFile-FileName flex-grow-1 justify-content-start text-primary">{name || 'Untitled'}</span>
          </Tippy>
          {messageMap[stage]}
          {size && <span className="GenericFile-FileSize">- {bytesToSize(size)}</span>}
        </div>
        <div>
          <ProgressBar
            now={progress || 0}
            variant={error ? 'danger' : progress < 1 ? 'primary' : 'success'}
            style={{
              height: '0.25rem',
            }}
          />
        </div>
        <div>{children}</div>
        {onDelete && (
          <Button
            className="GenericFile-Delete rounded-circle p-0 d-flex justify-content-center align-items-center"
            size="sm"
            variant={error ? 'danger' : 'primary'}
            onClick={onDelete}
            style={{ position: 'absolute', top: '-0.75rem', right: '-0.75rem', width: '1.5rem', height: '1.5rem' }}
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </Card.Body>
      {canPreview && (
        <Card.Body
          as={Link}
          className="GenericFile-Preview d-flex flex-grow-0 justify-content-center align-items-center border-start px-2 text-decoration-none"
          to={preview}
          target="_blank"
          style={{
            display: canPreview ? 'block' : 'none',
            pointerEvents: canPreview ? 'all' : 'none',
            cursor: canPreview ? 'pointer' : 'not-allowed',
          }}
        >
          <i className="sv sv-eye fs-2" />
        </Card.Body>
      )}
    </Card>
  );
};

export default GenericFileUpload;
